import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button, Alert, TextField, Typography, Radio, RadioGroup, COLORS,
  Modal, ModalHeader, ModalContent, ModalFooter, ModalButtons,
} from 'pvs-design-system';
import { Box, Stack, LinearProgress } from '@mui/material';
import type { CategorySelectorItem } from 'types';
import type { ExcludedLocation } from 'types/LibraryApi';
import { SHIMMER_PURPLE_SX } from 'constants/styles';
import TreeSelectField from 'components/virtual-library/TreeSelectField';
import ExcludeLocationsField from 'components/virtual-library/ExcludeLocationsField';
import ModalRouteBackground from 'components/virtual-library/ModalRouteBackground';
import UnsavedChangesModal from 'components/virtual-library/UnsavedChangesModal';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import { decodeHtmlEntities } from 'utils/htmlEntities';
import { useAdminFormTrees, useModalClose } from 'hooks';
import {
  useCategoryEditInitQuery,
  useUpdateAdminCategoryMutation,
} from 'api/useLibraryApi';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetCategoryExcludedLocationsQuery } from 'api/libraryApi';
import { useLazyGetCategoryExclusionHierarchyVerifyQuery } from 'api/libraryApi';
import { ROUTES } from 'constants/constants';

const MAX_NAME_LENGTH = 50;

const validateName = (value: string): string => {
  if (!value.trim()) return 'Name is required.';
  if (value.trim().length > MAX_NAME_LENGTH) {
    return `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
  }
  return '';
};

const EditCategoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { editCategory?: string; editLineage?: string } | null;
  const editCategory = navState?.editCategory ?? '';
  const editLineage = navState?.editLineage ?? '/Home/';

  // ── Form state ─────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationError, setLocationError] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);
  const [excludedLocations, setExcludedLocations] = useState<ExcludedLocation[]>([]);
  const [formInitialized, setFormInitialized] = useState(false);
  const [locationHierarchyError, setLocationHierarchyError] = useState(false);
  const [verifyingLocation, setVerifyingLocation] = useState(false);
  const verifyRequestIdRef = useRef(0);

  // ── "Which Location?" (category-instance selector) ──────────────────
  // The id of the category instance the user has actively switched to; null
  // until they pick a different one. Driving the edit-init `idCategory` arg
  // with this makes RTK Query refetch + de-dupe, so the hook always reflects
  // the latest selection — race-safe without manual request tracking.
  const [selectedCategoryOverride, setSelectedCategoryOverride] = useState<number | null>(null);
  // Last non-empty selector list, kept so the radios stay visible during a
  // switch refetch (the new arg's cache entry is momentarily empty).
  const [selectorCache, setSelectorCache] = useState<CategorySelectorItem[]>([]);
  // Which instance the form has been populated for — guards re-population so a
  // switch re-fills the form exactly once when its data arrives.
  const populatedForCategoryIdRef = useRef<number | null>(null);

  // Snapshot of form values at load time — used for dirty tracking.
  const initialValuesRef = useRef<{
    name: string;
    locationId: number | null;
    parentCategoryId: number | null;
    excludedLocations: ExcludedLocation[];
  } | null>(null);
  
  // Tree state and lazy loaders
  const {
    locationNodes,
    setLocationNodes,
    handleLoadLocationChildren,
    categoryNodes,
    fetchCategoriesForLocation,
    handleLoadCategoryChildren,
  } = useAdminFormTrees();

  // ── API queries ────────────────────────────────────────────────────
  const {
    categorySelector,
    locationTreeNodes,
    categoryId: serverCategoryId,
    defaultLocationId,
    defaultParentCategoryId,
    isLoading: initLoading,
    isFetching: initFetching,
    isError: initError,
    error: initErrorObj,
  } = useCategoryEditInitQuery(editLineage, editCategory, selectedCategoryOverride);

  // Keep the last non-empty selector list so the "Which Location?" radios
  // persist across switch refetches (RTK clears data for a new arg key).
  useEffect(() => {
    if (categorySelector.length > 0) setSelectorCache(categorySelector);
  }, [categorySelector]);

  // Effective selected instance — the optimistic override wins for instant
  // radio feedback; otherwise the server's current selection.
  const selectedInstanceId = selectedCategoryOverride ?? serverCategoryId;

  // If init data fails to load, show a toast and navigate back to library
  useEffect(() => {
    if (initError) {
      Notify.error(extractErrorMessage(initErrorObj, 'Failed to load folder. It may have been deleted.'));
      navigate(ROUTES.library);
    }
  }, [initError, initErrorObj, navigate]);

  // Fetch previously-saved excluded locations once we have the category ID.
  // refetchOnMountOrArgChange forces a fresh fetch every time the Edit modal
  // opens, so locations changed in a previous edit show up immediately without a
  // hard refresh (the cached value is otherwise served stale on reopen).
  const {
    data: savedExcludedLocations,
    isFetching: excludedLocationsFetching,
    isError: excludedLocationsError,
  } = useGetCategoryExcludedLocationsQuery(
    // skipToken (rather than a non-null-asserted arg) keeps this type-safe: the
    // query is skipped until edit-init resolves serverCategoryId, so { idCategory }
    // is only ever built from a real number — no reliance on the skip flag matching.
    serverCategoryId === null ? skipToken : { idCategory: serverCategoryId },
    { refetchOnMountOrArgChange: true },
  );

  // Excluded locations are "settled" for the current category once the query is no
  // longer fetching and has either returned data or errored (an error is treated as
  // no exclusions). The form stays behind the shimmer until this is true, so the
  // saved exclusions are pre-populated atomically with the rest of the form — there
  // is no interactive window where a user edit could be overwritten when the query
  // later resolves.
  const excludedLocationsSettled =
    serverCategoryId !== null &&
    !excludedLocationsFetching &&
    (savedExcludedLocations !== undefined || excludedLocationsError);

  // Sync init locations into lazily-loaded state
  React.useEffect(() => {
    if (locationTreeNodes.length > 0) setLocationNodes(locationTreeNodes);
  }, [locationTreeNodes]);

  const [updateCategory, { isLoading: saving }] = useUpdateAdminCategoryMutation();

  const [verifyExclusionHierarchy] = useLazyGetCategoryExclusionHierarchyVerifyQuery();

  // Auto-clear the hierarchy error once excluded locations are removed.
  useEffect(() => {
    if (excludedLocations.length === 0) setLocationHierarchyError(false);
  }, [excludedLocations]);

  // ── Pre-fill form when data loads, and re-fill when the user switches to a
  //    different "Which Location?" instance ─────────────────────────────────
  // Keyed on the loaded instance id so it runs once per instance: on first load
  // and again whenever the edit-init refetch resolves for a newly picked one.
  useEffect(() => {
    if (
      editCategory &&
      serverCategoryId !== null &&
      defaultLocationId !== null &&
      !initFetching &&
      excludedLocationsSettled &&
      populatedForCategoryIdRef.current !== serverCategoryId
    ) {
      populatedForCategoryIdRef.current = serverCategoryId;
      setName(editCategory);
      setNameError('');
      setLocationId(defaultLocationId);
      setLocationError('');
      setLocationHierarchyError(false);
      setParentCategoryId(defaultParentCategoryId);
      // Exclusions are guaranteed loaded here (excludedLocationsSettled gates this
      // effect), so populate them atomically with the rest of the form. Full objects
      // round-trip the scope; an errored query is treated as no exclusions.
      const loadedExclusions = savedExcludedLocations ?? [];
      setExcludedLocations(loadedExclusions);
      fetchCategoriesForLocation(defaultLocationId);
      initialValuesRef.current = {
        name: editCategory,
        locationId: defaultLocationId,
        parentCategoryId: defaultParentCategoryId,
        excludedLocations: loadedExclusions,
      };
      setFormInitialized(true);
    }
  }, [editCategory, serverCategoryId, defaultLocationId, defaultParentCategoryId, initFetching, excludedLocationsSettled, savedExcludedLocations, fetchCategoriesForLocation]);

  // ── Dirty state ───────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    const init = initialValuesRef.current;
    if (!init) return false;
    if (name.trim() !== init.name) return true;
    if (locationId !== init.locationId) return true;
    if (parentCategoryId !== init.parentCategoryId) return true;
    const normExcluded = (arr: ExcludedLocation[]) =>
      arr.map((e) => `${e.idLocation}:${e.enabled ? 1 : 0}:${e.childrenEnabled ? 1 : 0}`).sort();
    const cur = normExcluded(excludedLocations);
    const initEx = normExcluded(init.excludedLocations);
    if (cur.length !== initEx.length || cur.some((e, i) => e !== initEx[i])) return true;
    return false;
  }, [name, locationId, parentCategoryId, excludedLocations]);

  // ── Helpers ────────────────────────────────────────────────────────
  const validate = (): boolean => {
    let valid = true;

    const nErr = validateName(name);
    setNameError(nErr);
    if (nErr) valid = false;

    if (locationId === null) {
      setLocationError('Location is required.');
      valid = false;
    } else {
      setLocationError('');
    }

    return valid;
  };

  const handleLocationSelect = useCallback(async (locId: number) => {
    if (excludedLocations.length > 0 && serverCategoryId !== null) {
      const requestId = ++verifyRequestIdRef.current;
      setVerifyingLocation(true);
      try {
        const result = await verifyExclusionHierarchy(
          { idLocation: locId, idCategory: serverCategoryId },
        ).unwrap();
        if (verifyRequestIdRef.current !== requestId) return;
        if (!result.isValid) {
          setLocationHierarchyError(true);
          return;
        }
      } catch {
        if (verifyRequestIdRef.current !== requestId) return;
      } finally {
        if (verifyRequestIdRef.current === requestId) setVerifyingLocation(false);
      }
    }
    setLocationHierarchyError(false);
    setLocationId(locId);
    setLocationError('');
    setParentCategoryId(null);
    // Fetch parent categories scoped to the selected location via API.
    fetchCategoriesForLocation(locId);
  }, [excludedLocations.length, serverCategoryId, verifyExclusionHierarchy, fetchCategoriesForLocation]);

  // Switch which category instance ("Which Location?") is being edited. RTK
  // Query refetches edit-init with the new idCategory and the form re-fills
  // via the population effect above once the fresh data arrives.
  const handleWhichLocationSelect = (categoryId: number) => {
    if (categoryId === selectedInstanceId) return; // already editing this one
    setSelectedCategoryOverride(categoryId);
  };

  const handleSave = async () => {
    if (!validate() || serverCategoryId === null) return;

    try {
      await updateCategory({
        id: String(serverCategoryId),
        name: name.trim(),
        locationId: locationId!,
        parentCategoryId,
        displayColumns: 1,
        excludedLocations,
      }).unwrap();

      Notify.success('Folder updated successfully.');
      navigate(-1);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to update folder. Please try again.'));
    }
  };

  const closeModal = useModalClose();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // If the form has unsaved changes, confirm before closing; otherwise close directly.
  const handleClose = () => {
    if (isDirty) { setShowUnsavedModal(true); return; }
    closeModal();
  };

  // Keep the shimmer up until the whole form is ready. formInitialized flips true
  // only once edit-init AND the excluded-locations query have both settled (the
  // populate effect is gated on excludedLocationsSettled), so the form is never
  // revealed half-populated. On an instance switch formInitialized is already true,
  // so the inline LinearProgress handles that case instead of a full skeleton.
  if (editCategory && !initError && !formInitialized) {
    const shimmerSx = SHIMMER_PURPLE_SX;
    return (
      <>
      <ModalRouteBackground />
      <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="edit-folder-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
        <ModalHeader id="edit-folder-title" title="Edit folder" onClose={handleClose} />
        <ModalContent>
          <Stack gap={3}>
            {/* Name field skeleton */}
            <Box>
              <Box sx={{ ...shimmerSx, width: 50, height: 14, mb: 1 }} />
              <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
            </Box>
            {/* Location field skeleton */}
            <Box>
              <Box sx={{ ...shimmerSx, width: 70, height: 14, mb: 1 }} />
              <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
            </Box>
            {/* Parent Category field skeleton */}
            <Box>
              <Box sx={{ ...shimmerSx, width: 120, height: 14, mb: 1 }} />
              <Box sx={{ ...shimmerSx, width: '100%', height: 40 }} />
            </Box>
            {/* Exclude from locations skeleton */}
            <Box>
              <Box sx={{ ...shimmerSx, width: 150, height: 14, mb: 1 }} />
              <Box sx={{ ...shimmerSx, width: 140, height: 20 }} />
            </Box>
            {/* Action buttons skeleton */}
            <Stack direction="row" gap={1.5} pt={1}>
              <Box sx={{ ...shimmerSx, width: 90, height: 40, borderRadius: 1 }} />
              <Box sx={{ ...shimmerSx, width: 130, height: 40, borderRadius: 1 }} />
            </Stack>
          </Stack>
        </ModalContent>
      </Modal>
      </>
    );
  }

  if (initError || (!initLoading && !editCategory)) {
    // Error is handled via toast + navigate in the useEffect above.
    // This return prevents rendering the form while navigating away.
    return null;
  }

  return (
    <>
    <ModalRouteBackground />
    <Modal open onClose={handleClose} closeModalOnBackdropClick ariaLabelledby="edit-folder-title" PaperProps={{ sx: { width: '800px', maxWidth: '800px' } }}>
      <ModalHeader id="edit-folder-title" title="Edit folder" onClose={handleClose} />

      <ModalContent>
      {/* ── Form ────────────────────────────────────────────────────── */}
      <form
        id="edit-folder-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <Stack gap={3}>
          {/* Name */}
          <TextField
            id="edit-cat-name"
            label="Name"
            value={name}
            size="medium"
            fullWidth
            // Locked while a "Which Location?" switch is (re)loading. The populate
            // effect re-fills every field (including Name) only once the new
            // instance's edit-init AND excluded-locations queries have both settled,
            // so an edit made in that window would be silently overwritten.
            // initFetching alone is not enough — it can clear before the
            // excluded-locations query finishes, so gate on excludedLocationsSettled too.
            disabled={initFetching || !excludedLocationsSettled}
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
              if (nameError) setNameError(validateName(e.target.value));
            }}
            error={!!nameError}
            errorMessage={nameError}
          />

          {/* Which Location? — only shown when the folder exists at multiple
              locations; a single instance renders the location tree directly. */}
          {selectorCache.length > 1 && (
            <Box>
              <Typography
                variant="p-sm"
                style={{ fontWeight: 700, color: COLORS.gray[700], display: 'block', marginBottom: 6 }}
              >
                Which Location?
              </Typography>
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                <RadioGroup
                  id="edit-cat-which-location"
                  aria-label="Which location?"
                  name="edit-cat-which-location"
                  value={selectedInstanceId != null ? String(selectedInstanceId) : ''}
                  onChange={(_e: React.ChangeEvent<HTMLInputElement>, val: string) =>
                    handleWhichLocationSelect(Number(val))
                  }
                >
                  {selectorCache.map((item) => (
                    <Radio
                      key={item.id}
                      id={`edit-cat-which-location-${item.id}`}
                      name="edit-cat-which-location"
                      value={String(item.id)}
                      label={decodeHtmlEntities(item.locationName)}
                    />
                  ))}
                </RadioGroup>
              </Box>
              {initFetching && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
            </Box>
          )}

          {/* Location */}
          <TreeSelectField
            id="edit-cat-location"
            label="Location"
            nodes={locationNodes}
            selectedId={locationId}
            onSelect={handleLocationSelect}
            onLoadChildren={handleLoadLocationChildren}
            placeholder="Select a location…"
            loading={initLoading || verifyingLocation || initFetching}
            disabled={verifyingLocation || initFetching || !excludedLocationsSettled}
            error={!!locationError || initError}
            errorMessage={locationError || (initError ? 'Failed to load locations.' : undefined)}
          />
          {locationHierarchyError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Folder&apos;s location must be at or above any excluded locations.
              To change the location, please first remove the excluded locations and save before proceeding.
            </Alert>
          )}

          {/* Parent Category */}
          <TreeSelectField
            id="edit-cat-parent"
            label="Parent Folder"
            nodes={categoryNodes}
            selectedId={parentCategoryId}
            onSelect={setParentCategoryId}
            onLoadChildren={handleLoadCategoryChildren}
            placeholder="Choose a Library Resource Folder"
            disabled={locationId === null || initFetching || !excludedLocationsSettled}
            disabledPlaceholder="Select a location first"
            excludeId={serverCategoryId}
          />

          {/* Exclude from locations */}
          <ExcludeLocationsField
            value={excludedLocations}
            locationId={locationId}
            onChange={setExcludedLocations}
            isCreateMode={false}
            disabled={initFetching || !excludedLocationsSettled}
            helperText="Use this to identify any locations that should not have access to the folder regardless of if they otherwise have permission to it."
          />
        </Stack>
      </form>
      </ModalContent>

      <ModalFooter>
        <ModalButtons>
          <Button type="button" size="small" variant="text" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button type="submit" size="small" form="edit-folder-form" variant="contained" color="primary" disabled={saving || !isDirty}>Save Changes</Button>
        </ModalButtons>
      </ModalFooter>
    </Modal>
    <UnsavedChangesModal
      open={showUnsavedModal}
      itemLabel="folder"
      onKeepEditing={() => setShowUnsavedModal(false)}
      onDiscard={() => { setShowUnsavedModal(false); closeModal(); }}
    />
    </>
  );
};

export default EditCategoryPage;
